import generatedPosts from "../generated/blog-posts.json";
import { LANGS, type Lang } from "./translations";

export interface BlogPostFrontmatter {
  title: string;
  description: string;
  date: string;
  draft?: boolean;
  tags?: string[];
  author?: string;
  ogImage?: string;
}

export interface BlogPost {
  slug: string;
  lang: Lang;
  frontmatter: BlogPostFrontmatter;
  bodyMD: string;
  readingMinutes: number;
  category: string;
  hero?: BlogHeroImage;
}

export interface BlogHeroImage {
  src: string;
  alt: string;
  width: number;
  height: number;
  caption?: string;
  credit?: string;
}

type CMSTag = string | { slug?: string; name?: string };

type CMSFrontmatterTranslation = {
  title?: string;
  description?: string;
  excerpt?: string;
  bodyMD?: string;
  date?: string;
  tags?: string[];
  author?: string;
  ogImage?: string;
  draft?: boolean;
};

type CMSHeroImage = Partial<BlogHeroImage> & {
  Src?: string;
  Alt?: string;
  Width?: number;
  Height?: number;
  Caption?: string;
  Credit?: string;
};

export type CMSPost = {
  slug?: string;
  lang?: Lang | string;
  language?: string;
  locale?: string;
  title?: string;
  excerpt?: string;
  description?: string;
  bodyMD?: string;
  publishedAt?: string;
  updatedAt?: string;
  readTime?: number;
  readingMinutes?: number;
  translations?: Partial<Record<Lang, CMSFrontmatterTranslation>>;
  coverImageURL?: string;
  hero?: CMSHeroImage | null;
  category?: { slug?: string; name?: string } | string | null;
  tags?: CMSTag[];
  frontmatter?: {
    seoDescription?: string;
    keywords?: string[];
    source?: string;
    translations?: Partial<Record<Lang, CMSFrontmatterTranslation>>;
  } | null;
};

const LEGACY_STATIC_SOURCE = "meeting-transcriber/apps/site/content/blog";

function normalizeLang(value: unknown): Lang | null {
  if (typeof value !== "string") return null;
  const lower = value.toLowerCase();
  if (lower.startsWith("pt")) return "pt";
  if (lower.startsWith("es")) return "es";
  if (lower.startsWith("en")) return "en";
  return null;
}

function explicitLang(post: CMSPost): Lang | null {
  return normalizeLang(post.lang) || normalizeLang(post.language) || normalizeLang(post.locale);
}

function isStaticFallback(post: CMSPost): boolean {
  return post.frontmatter?.source === LEGACY_STATIC_SOURCE;
}

function normalizeHero(post: CMSPost): BlogHeroImage | undefined {
  const hero = post.hero;
  const src = hero?.src || hero?.Src || post.coverImageURL;
  if (!src) return undefined;
  return {
    src,
    alt: hero?.alt || hero?.Alt || post.title || "",
    width: hero?.width || hero?.Width || 1200,
    height: hero?.height || hero?.Height || 630,
    caption: hero?.caption || hero?.Caption,
    credit: hero?.credit || hero?.Credit,
  };
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function tagNames(tags: CMSTag[] | undefined): string[] {
  if (!tags) return [];
  return tags
    .map((tag) => {
      if (typeof tag === "string") return UUID_RE.test(tag) ? "" : tag;
      return tag.name || tag.slug || "";
    })
    .filter(Boolean);
}

function firstText(bodyMD: string | undefined): string {
  if (!bodyMD) return "";
  const text = bodyMD
    .split("\n")
    .map((line) => line.replace(/^#+\s*/, "").replace(/^>\s*/, "").trim())
    .find((line) => line && !line.startsWith("```"));
  return text || "";
}

function categoryName(category: CMSPost["category"]): string {
  if (!category) return "Blog";
  if (typeof category === "string") return category;
  return category.name || category.slug || "Blog";
}

function readingMinutesFor(post: CMSPost, localizedBodyMD?: string): number {
  const configured = post.readTime || post.readingMinutes;
  if (configured && configured > 0) return Math.ceil(configured);
  const words = (localizedBodyMD || post.bodyMD || "").trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 220));
}

function baseFrontmatter(post: CMSPost): BlogPostFrontmatter {
  const tags = tagNames(post.tags);
  const description =
    post.frontmatter?.seoDescription ||
    post.description ||
    post.excerpt ||
    firstText(post.bodyMD) ||
    "";

  return {
    title: post.title || post.slug || "Untitled",
    description,
    date: post.publishedAt || post.updatedAt || new Date(0).toISOString(),
    tags,
    ogImage: normalizeHero(post)?.src,
  };
}

function localizedPost(post: CMSPost, lang: Lang): BlogPost | null {
  const slug = post.slug;
  if (!slug) return null;
  const base = baseFrontmatter(post);
  const translation = post.translations?.[lang] || post.frontmatter?.translations?.[lang];
  const postLang = explicitLang(post);
  if (postLang && lang !== postLang && !translation) return null;
  if (isStaticFallback(post) && !translation) return null;
  if (lang !== "en" && lang !== postLang && !translation) return null;
  if (translation?.draft) return null;

  const frontmatter: BlogPostFrontmatter = {
    ...base,
    title: translation?.title || base.title,
    description: translation?.description || translation?.excerpt || base.description,
    date: translation?.date || base.date,
    tags: translation?.tags || base.tags,
    author: translation?.author || base.author,
    ogImage: translation?.ogImage || base.ogImage,
  };

  return {
    slug,
    lang,
    frontmatter,
    bodyMD: translation?.bodyMD || post.bodyMD || "",
    readingMinutes: readingMinutesFor(post, translation?.bodyMD),
    category: categoryName(post.category),
    hero: normalizeHero(post),
  };
}

export function expandCMSPosts(posts: CMSPost[]): BlogPost[] {
  return posts.flatMap((post) =>
    LANGS.map((lang) => localizedPost(post, lang)).filter((p): p is BlogPost => p !== null),
  );
}

const ALL_POSTS = expandCMSPosts(generatedPosts as CMSPost[]);
const NOINDEX_POST_SLUGS = new Set(["whisper-local-apple-silicon"]);

export function getPosts(lang: Lang): BlogPost[] {
  return ALL_POSTS
    .filter((p) => p.lang === lang)
    .sort((a, b) => b.frontmatter.date.localeCompare(a.frontmatter.date));
}

export function getPost(lang: Lang, slug: string): BlogPost | null {
  return ALL_POSTS.find((p) => p.lang === lang && p.slug === slug) ?? null;
}

export function listAll(): BlogPost[] {
  return ALL_POSTS;
}

export function isNoindexPostSlug(slug: string): boolean {
  return NOINDEX_POST_SLUGS.has(slug);
}
